import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { X, ArrowRight, ExternalLink, ChevronLeft, ChevronRight } from 'lucide-react';
import { Project } from '../types';
import { projectsData } from '../data';
import useIsMobile from '../hooks/useIsMobile';

function getYouTubeEmbedUrl(url: string): string | null {
  if (!url) return null;
  if (url.includes('youtube.com') || url.includes('youtu.be')) {
    let videoId = '';
    if (url.includes('v=')) {
      const parts = url.split('v=');
      if (parts[1]) {
        videoId = parts[1].split('&')[0];
      }
    } else if (url.includes('embed/')) {
      const parts = url.split('embed/');
      if (parts[1]) {
        videoId = parts[1].split('?')[0];
      }
    } else if (url.includes('youtu.be/')) {
      const parts = url.split('youtu.be/');
      if (parts[1]) {
        videoId = parts[1].split('?')[0];
      }
    } else if (url.includes('shorts/')) {
      const parts = url.split('shorts/');
      if (parts[1]) {
        videoId = parts[1].split('?')[0];
      }
    }
    if (videoId) {
      return `https://www.youtube.com/embed/${videoId}`;
    }
  }
  return null;
}

function getYouTubeThumbnail(url: string): string | null {
  const embedUrl = getYouTubeEmbedUrl(url);
  if (!embedUrl) return null;
  const videoId = embedUrl.split('/').pop();
  return `https://img.youtube.com/vi/${videoId}/hqdefault.jpg`;
}

interface ProjectCardProps {
  key?: string;
  project: Project;
  isMobile: boolean;
  onSelect: (project: Project) => void;
}

function ProjectCard({ project, isMobile, onSelect }: ProjectCardProps) {
  const imagesList = React.useMemo(() => {
    return project.gallery && project.gallery.length > 0
      ? project.gallery
      : [project.image];
  }, [project.gallery, project.image]);

  const [currentIdx, setCurrentIdx] = useState(0);
  const [direction, setDirection] = useState(1); // 1 = next, -1 = prev

  useEffect(() => {
    if (imagesList.length <= 1) return;
    const interval = setInterval(() => {
      setDirection(1);
      setCurrentIdx((prev) => (prev + 1) % imagesList.length);
    }, 6500); // Slide auto-transitions every 6.5 seconds
    return () => clearInterval(interval);
  }, [imagesList]);

  const handleNext = (e: React.MouseEvent) => {
    e.stopPropagation();
    setDirection(1);
    setCurrentIdx((prev) => (prev + 1) % imagesList.length);
  };

  const handlePrev = (e: React.MouseEvent) => {
    e.stopPropagation();
    setDirection(-1);
    setCurrentIdx((prev) => (prev - 1 + imagesList.length) % imagesList.length);
  };

  const handleDotClick = (e: React.MouseEvent, index: number) => {
    e.stopPropagation();
    setDirection(index > currentIdx ? 1 : -1);
    setCurrentIdx(index);
  };

  const slideVariants = {
    enter: (dir: number) => ({
      x: dir > 0 ? '100%' : '-100%',
      opacity: 0.9,
      zIndex: 1
    }),
    center: {
      x: 0,
      opacity: 1,
      zIndex: 2,
      transition: {
        x: { type: "tween", ease: "easeInOut", duration: 1.2 },
        opacity: { duration: 0.8 }
      }
    },
    exit: (dir: number) => ({
      x: dir < 0 ? '-100%' : '100%',
      opacity: 0.8,
      zIndex: 0,
      transition: {
        x: { type: "tween", ease: "easeInOut", duration: 1.2 },
        opacity: { duration: 0.8 }
      }
    })
  };

  return (
    <motion.div
      layout={!isMobile}
      initial={isMobile ? { opacity: 1, y: 0, scale: 1 } : { opacity: 0, y: 70, scale: 0.92 }}
      whileInView={{ opacity: 1, y: 0, scale: 1 }}
      whileHover={isMobile ? {} : { 
        y: -10, 
        scale: 1.025,
        boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.15)"
      }}
      viewport={{ once: false, amount: 0.12 }}
      exit={isMobile ? { opacity: 1 } : { opacity: 0, scale: 0.92 }}
      transition={isMobile ? { duration: 0.15 } : { type: "spring", stiffness: 100, damping: 17, mass: 1 }}
      onClick={() => onSelect(project)}
      className={`group relative cursor-pointer flex flex-col justify-between bg-neutral-50 dark:bg-neutral-900/10 border border-neutral-100 dark:border-neutral-800/40 p-5 rounded-2xl md:p-6 transition-all duration-300 ${isMobile ? '' : 'hover:border-emerald-500/40 hover:bg-white dark:hover:bg-neutral-900/30'} overflow-hidden h-full`}
      data-cursor="view"
      data-cursor-text="EXPLORE"
    >
      {/* Year tag upper left */}
      <div className="mb-6 flex justify-between items-center z-10">
        <span className="font-mono text-[10px] font-bold tracking-wider text-neutral-500 dark:text-neutral-400">
          © {project.year}
        </span>
      </div>

      {/* Cover Image inside frame - slideshow */}
      <div className="relative h-64 sm:h-72 w-full rounded-xl overflow-hidden mb-6 bg-neutral-200 dark:bg-neutral-800">
        <AnimatePresence initial={false} custom={direction}>
          {(() => {
            const ytThumb = getYouTubeThumbnail(imagesList[currentIdx]);
            if (ytThumb) {
              return (
                <motion.img
                  key={currentIdx}
                  custom={direction}
                  variants={slideVariants}
                  initial="enter"
                  animate="center"
                  exit="exit"
                  src={ytThumb}
                  alt={`${project.title} preview slide ${currentIdx + 1}`}
                  referrerPolicy="no-referrer"
                  className="absolute inset-0 w-full h-full object-cover"
                />
              );
            }
            if (imagesList[currentIdx].endsWith('.mp4') || imagesList[currentIdx].includes('.mp4')) {
              return (
                <motion.video
                  key={currentIdx}
                  custom={direction}
                  variants={slideVariants}
                  initial="enter"
                  animate="center"
                  exit="exit"
                  src={imagesList[currentIdx]}
                  autoPlay
                  muted
                  loop
                  playsInline
                  className="absolute inset-0 w-full h-full object-cover"
                />
              );
            }
            return (
              <motion.img
                key={currentIdx}
                custom={direction}
                variants={slideVariants}
                initial="enter"
                animate="center"
                exit="exit"
                src={imagesList[currentIdx]}
                alt={`${project.title} preview slide ${currentIdx + 1}`}
                referrerPolicy="no-referrer"
                className="absolute inset-0 w-full h-full object-cover"
              />
            );
          })()}
        </AnimatePresence>

        {/* Slideshow arrows visible on hover */}
        {imagesList.length > 1 && (
          <>
            <button
              onClick={handlePrev}
              type="button"
              className="absolute left-3 top-1/2 -translate-y-1/2 p-1.5 rounded-full bg-black/50 hover:bg-emerald-500 text-white font-bold transition-all duration-300 md:opacity-0 md:group-hover:opacity-100 z-20 focus:outline-none"
              title="Previous slide"
            >
              <ChevronLeft className="w-4 h-4" />
            </button>
            <button
              onClick={handleNext}
              type="button"
              className="absolute right-3 top-1/2 -translate-y-1/2 p-1.5 rounded-full bg-black/50 hover:bg-emerald-500 text-white font-bold transition-all duration-300 md:opacity-0 md:group-hover:opacity-100 z-20 focus:outline-none"
              title="Next slide"
            >
              <ChevronRight className="w-4 h-4" />
            </button>
          </>
        )}

        {/* Elegant indicator dots at bottom */}
        {imagesList.length > 1 && (
          <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-1 z-20 bg-black/30 backdrop-blur-sm px-2 py-1 rounded-full">
            {imagesList.map((_, idx) => (
              <button
                key={idx}
                onClick={(e) => handleDotClick(e, idx)}
                type="button"
                className={`h-1 rounded-full transition-all duration-300 ${
                  currentIdx === idx
                    ? 'w-4 bg-emerald-500'
                    : 'w-1 bg-white/60 hover:bg-white'
                }`}
              />
            ))}
          </div>
        )}
      </div>

      {/* Metadata content */}
      <div className="z-10 flex justify-between items-end mt-2">
        <div>
          <span className="font-mono text-[9px] tracking-widest text-emerald-500 font-bold uppercase block mb-1">
            {project.client}
          </span>
          <h3 className="text-lg font-sans font-semibold tracking-tight text-neutral-900 dark:text-white group-hover:text-emerald-500 transition-colors">
            {project.title}
          </h3>
          <p className="text-xs text-neutral-500 dark:text-neutral-400 font-sans mt-1 line-clamp-1">
            {project.description}
          </p>
        </div>
        <div className="ml-4 p-3 rounded-full border border-neutral-200 dark:border-neutral-800 dark:group-hover:border-emerald-500/40 text-neutral-400 dark:text-neutral-400 group-hover:bg-emerald-500 group-hover:text-white transition-all duration-300 shrink-0">
          <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
        </div>
      </div>
    </motion.div>
  );
}

export default function WorkShowcase() {
  const [selectedCategory, setSelectedCategory] = useState<string>('ALL');
  const [selectedProject, setSelectedProject] = useState<Project | null>(null);
  const [isLaunching, setIsLaunching] = useState<boolean>(false);
  const [lightboxUrl, setLightboxUrl] = useState<string | null>(null);
  const isMobile = useIsMobile();

  const slideableMedia = selectedProject
    ? Array.from(new Set([
        selectedProject.image,
        ...selectedProject.gallery.filter(url => !getYouTubeEmbedUrl(url))
      ]))
    : [];

  const currentIndex = slideableMedia.indexOf(lightboxUrl || '');

  // Listen for Escape key and Arrow keys to close or navigate modal/lightbox
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        if (lightboxUrl) {
          setLightboxUrl(null);
        } else if (selectedProject) {
          setSelectedProject(null);
        }
      } else if (lightboxUrl && slideableMedia.length > 1) {
        if (e.key === 'ArrowLeft') {
          const prevIdx = (currentIndex - 1 + slideableMedia.length) % slideableMedia.length;
          setLightboxUrl(slideableMedia[prevIdx]);
        } else if (e.key === 'ArrowRight') {
          const nextIdx = (currentIndex + 1) % slideableMedia.length;
          setLightboxUrl(slideableMedia[nextIdx]);
        }
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [lightboxUrl, selectedProject, slideableMedia, currentIndex]);

  const categories = ['ALL', 'BRANDING', 'WEB DESIGN', 'UI/UX', 'MOTION'];

  const filteredProjects = selectedCategory === 'ALL'
    ? projectsData
    : projectsData.filter(p => p.category.toUpperCase() === selectedCategory);

  // Disable body scroll when a case study modal is active
  useEffect(() => {
    if (selectedProject) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [selectedProject]);

  return (
    <section id="work" className="py-24 scroll-mt-24 relative overflow-hidden bg-transparent text-neutral-900 dark:text-white transition-colors duration-500">
      {/* Background Ambience / Blur Overlays replicating premium high-end portfolio layout */}
      <div className="absolute top-0 left-0 w-full h-full bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-emerald-950/20 via-neutral-950/20 to-neutral-950 pointer-events-none opacity-0 dark:opacity-100 transition-opacity duration-500" />
      <div className="absolute -top-40 right-20 w-[450px] h-[450px] bg-emerald-500/5 blur-[130px] rounded-full pointer-events-none opacity-0 dark:opacity-100 transition-opacity duration-500" />
      <div className="absolute -bottom-40 left-10 w-[550px] h-[550px] bg-emerald-500/5 blur-[150px] rounded-full pointer-events-none opacity-0 dark:opacity-100 transition-opacity duration-500" />

      <div className="max-w-7xl mx-auto px-6 md:px-12 relative z-10">
                {/* Section Header */}
        <div className="flex flex-col md:flex-row md:items-end justify-between mb-16 gap-8">
          <div className="flex flex-col items-start">
            <span className="font-mono text-[10px] tracking-[0.4em] text-emerald-500 font-extrabold uppercase block mb-4">
              CASE STUDIES
            </span>
            <h2 className="text-3xl sm:text-5xl font-sans tracking-tight text-neutral-900 dark:text-white font-medium hover:text-emerald-500 transition-colors duration-300 cursor-default">
              Selected Projects
            </h2>
            <motion.a
              id="raw-portfolio-drive-link"
              href="https://drive.google.com/drive/folders/1LZzq_H1cd6-tO3eXcwJOnsXrCIPyCMs2?usp=drive_link"
              target="_blank"
              rel="noopener noreferrer"
              whileHover={{ scale: 1.02, y: -2 }}
              whileTap={{ scale: 0.98 }}
              className="inline-flex items-center gap-2 mt-5 px-5 py-2.5 bg-neutral-950 dark:bg-emerald-500 hover:bg-neutral-800 dark:hover:bg-emerald-400 text-white dark:text-neutral-950 rounded-full font-mono text-[10px] sm:text-xs font-bold uppercase tracking-wider transition-all duration-300 shadow-[0_4px_20px_rgba(16,185,129,0.15)] outline-none"
            >
              <ExternalLink className="w-3.5 h-3.5" />
              <span>Raw Portfolio Dump</span>
            </motion.a>
          </div>
        </div>

        {/* Portfolio Grid Grid */}
        <motion.div
          layout
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 md:gap-12"
        >
          <AnimatePresence mode="popLayout">
            {filteredProjects.map((project) => (
              <ProjectCard
                key={project.id}
                project={project}
                isMobile={isMobile}
                onSelect={setSelectedProject}
              />
            ))}
          </AnimatePresence>
        </motion.div>
      </div>

      {/* Case Study Detail Sheet Overlay (Slide-Up Panel) */}
      <AnimatePresence>
        {selectedProject && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-neutral-950/80 backdrop-blur-md flex justify-end overflow-hidden"
          >
            {/* Click backdrop to close */}
            <div
              className="absolute inset-x-0 top-0 bottom-0 cursor-none"
              onClick={() => setSelectedProject(null)}
              data-cursor="close"
            />

            {/* Immersive Slide-Up Panel */}
            <motion.div
              key={selectedProject.id}
              initial={isMobile ? { opacity: 0 } : { x: '100%' }}
              animate={isMobile ? { opacity: 1 } : { x: 0 }}
              exit={isMobile ? { opacity: 0 } : { x: '100%' }}
              transition={isMobile ? { duration: 0.18, ease: 'easeOut' } : { type: 'spring', damping: 25, stiffness: 200, mass: 1 }}
              className="relative w-full lg:w-3/4 xl:w-2/3 h-full bg-white dark:bg-neutral-950 border-l border-neutral-200 dark:border-neutral-900 overflow-y-auto shadow-2xl z-20"
            >
              {/* Top Navigation Frame for Case Study */}
              <div className="sticky top-0 bg-white/80 dark:bg-neutral-950/80 backdrop-blur border-b border-neutral-200 dark:border-neutral-900 px-8 py-6 flex items-center justify-between z-30">
                <span className="font-mono text-xs font-bold tracking-widest text-neutral-500 dark:text-neutral-400 uppercase">
                  CASE STUDY
                </span>

                <button
                  onClick={() => setSelectedProject(null)}
                  className="p-3 rounded-full hover:bg-neutral-100 dark:hover:bg-neutral-900 transition-colors border border-neutral-200 dark:border-neutral-800 flex items-center justify-center gap-2"
                  data-cursor="close"
                  title="Close Case Study"
                >
                  <X className="w-4 h-4 text-neutral-800 dark:text-neutral-200" />
                  <span className="hidden sm:inline font-mono text-[10px] sm:text-xs font-bold tracking-wider">CLOSE</span>
                </button>
              </div>

              {/* Case Study Body */}
              <div className="p-8 sm:p-12 md:p-16 space-y-16">
                
                {/* Hero / Cover Visual */}
                <div className="h-64 sm:h-96 md:h-[450px] w-full rounded-2xl overflow-hidden relative shadow-2xl group/cover cursor-zoom-in">
                  <img
                    src={selectedProject.image}
                    alt={selectedProject.title}
                    referrerPolicy="no-referrer"
                    className="w-full h-full object-cover transition-transform duration-500 group-hover/cover:scale-105"
                    onClick={() => setLightboxUrl(selectedProject.image)}
                  />
                  <div className="absolute inset-0 bg-neutral-950/20 pointer-events-none" />
                </div>

                {/* Layout Stats Grid */}
                <div className="grid grid-cols-3 gap-8 py-8 border-y border-neutral-200 dark:border-neutral-900">
                  <div>
                    <span className="font-mono text-[9px] font-bold tracking-wider text-neutral-500 dark:text-neutral-400 uppercase block mb-1">CLIENT</span>
                    <span className="font-display text-sm font-bold text-neutral-800 dark:text-neutral-200">{selectedProject.client}</span>
                  </div>
                  <div>
                    <span className="font-mono text-[9px] font-bold tracking-wider text-neutral-500 dark:text-neutral-400 uppercase block mb-1">ROLE</span>
                    <span className="font-display text-sm font-bold text-neutral-800 dark:text-neutral-200">{selectedProject.role}</span>
                  </div>
                  <div>
                    <span className="font-mono text-[9px] font-bold tracking-wider text-neutral-500 dark:text-neutral-400 uppercase block mb-1">YEAR</span>
                    <span className="font-display text-sm font-bold text-neutral-800 dark:text-neutral-200">{selectedProject.year}</span>
                  </div>
                </div>

                {/* Overview & Core Title */}
                <div className="space-y-6">
                  <h2 className="text-3xl sm:text-5xl font-sans tracking-tight text-neutral-900 dark:text-white font-medium">
                    {selectedProject.title}
                  </h2>
                  <p className="text-lg text-neutral-500 dark:text-neutral-400 leading-relaxed font-light">
                    {selectedProject.description}
                  </p>
                </div>

                {/* Challenge and Solution Blocks */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-12 pt-8">
                  <div className="space-y-4">
                    <span className="font-mono text-xs text-red-500 font-bold tracking-wider uppercase flex items-center gap-2">
                       The Challenge
                    </span>
                    <p className="text-sm sm:text-base text-neutral-600 dark:text-neutral-400 leading-relaxed font-normal">
                      {selectedProject.challenge}
                    </p>
                  </div>

                  <div className="space-y-4">
                    <span className="font-mono text-xs text-emerald-500 font-bold tracking-wider uppercase flex items-center gap-2">
                       The Solution
                    </span>
                    <p className="text-sm sm:text-base text-neutral-600 dark:text-neutral-400 leading-relaxed font-normal">
                      {selectedProject.solution}
                    </p>
                  </div>
                </div>

                {/* Visual Showcase Gallery */}
                <div className="space-y-8 pt-8">
                  <span className="font-mono text-xs text-neutral-500 dark:text-neutral-400 font-bold tracking-[0.2em] uppercase block">
                    VISUAL GALLERY
                  </span>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                    {selectedProject.gallery.map((imgUrl, idx) => {
                      const ytEmbedUrl = getYouTubeEmbedUrl(imgUrl);
                      return (
                        <div
                          key={`${selectedProject.id}-gallery-item-${idx}`}
                          className={`rounded-xl overflow-hidden shadow-lg bg-neutral-100 dark:bg-neutral-900 ${
                            idx === 0 ? 'sm:col-span-2 h-72 sm:h-96' : 'h-64'
                          }`}
                        >
                          {ytEmbedUrl ? (
                            <iframe
                              src={ytEmbedUrl}
                              title={`${selectedProject.title} Exhibit ${idx + 1}`}
                              className="w-full h-full border-0"
                              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                              allowFullScreen
                            />
                          ) : imgUrl.endsWith('.mp4') || imgUrl.includes('.mp4') ? (
                            <video
                              src={imgUrl}
                              autoPlay
                              muted
                              loop
                              playsInline
                              controls
                              className="w-full h-full object-cover"
                            />
                          ) : (
                            <img
                              src={imgUrl}
                              alt={`${selectedProject.title} Exhibit ${idx + 1}`}
                              referrerPolicy="no-referrer"
                              className="w-full h-full object-cover transition-all duration-500 cursor-zoom-in hover:scale-105"
                              onClick={() => setLightboxUrl(imgUrl)}
                            />
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>


              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Lightbox / Full-size Image Viewer */}
      <AnimatePresence>
        {lightboxUrl && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 z-[100] bg-neutral-950/95 backdrop-blur-xl flex items-center justify-center p-4 select-none"
            onClick={() => setLightboxUrl(null)}
          >
            {/* Close button with high visibility */}
            <button
              onClick={() => setLightboxUrl(null)}
              className="absolute top-6 right-6 p-3 rounded-full bg-white/10 hover:bg-emerald-500 text-white transition-all duration-300 z-[110] border border-white/20 hover:border-emerald-500/40"
              title="Close Fullsize Image"
            >
              <X className="w-6 h-6" />
            </button>

            {/* Left navigation button */}
            {slideableMedia.length > 1 && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  const prevIdx = (currentIndex - 1 + slideableMedia.length) % slideableMedia.length;
                  setLightboxUrl(slideableMedia[prevIdx]);
                }}
                className="absolute left-4 md:left-8 p-3 md:p-4 rounded-full bg-white/10 hover:bg-emerald-500 text-white hover:text-neutral-950 transition-all duration-300 z-[110] border border-white/20 hover:border-emerald-500/40 font-bold flex items-center justify-center hover:scale-110 active:scale-95 cursor-pointer"
                title="Previous Image"
              >
                <ChevronLeft className="w-6 h-6 md:w-8 md:h-8" />
              </button>
            )}

            {/* Right navigation button */}
            {slideableMedia.length > 1 && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  const nextIdx = (currentIndex + 1) % slideableMedia.length;
                  setLightboxUrl(slideableMedia[nextIdx]);
                }}
                className="absolute right-4 md:right-8 p-3 md:p-4 rounded-full bg-white/10 hover:bg-emerald-500 text-white hover:text-neutral-950 transition-all duration-300 z-[110] border border-white/20 hover:border-emerald-500/40 font-bold flex items-center justify-center hover:scale-110 active:scale-95 cursor-pointer"
                title="Next Image"
              >
                <ChevronRight className="w-6 h-6 md:w-8 md:h-8" />
              </button>
            )}

            {/* Lightbox Main Image container */}
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              transition={{ type: "spring", damping: 25, stiffness: 300 }}
              className="relative max-w-full max-h-[90vh] flex items-center justify-center px-12"
              onClick={(e) => e.stopPropagation()}
            >
              {lightboxUrl.endsWith('.mp4') || lightboxUrl.includes('.mp4') ? (
                <video
                  src={lightboxUrl}
                  autoPlay
                  controls
                  playsInline
                  className="max-w-full max-h-[85vh] rounded-lg object-contain shadow-2xl"
                />
              ) : (
                <img
                  src={lightboxUrl}
                  alt="Full view"
                  referrerPolicy="no-referrer"
                  className="max-w-full max-h-[85vh] rounded-lg object-contain shadow-2xl select-none cursor-zoom-out"
                  onClick={() => setLightboxUrl(null)}
                />
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
}
